import React, { useState, useEffect, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import type { QuantSignal, QuantSummaryItem, QuantSummaryResponse } from '../types';
import {
  signalLabel, signalColor, signalBg,
  thStyle, tdStyle, tabStyle, tabActiveStyle, filterStyle,
  previewBaseColDefs,
  type PreviewOrder, type BaseAmtInfo,
} from './quantCommon';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

const filterActiveStyle: React.CSSProperties = {
  ...filterStyle, backgroundColor: '#3b82f6', color: 'white',
};
const selectStyle: React.CSSProperties = {
  padding: '4px 8px', borderRadius: '3px', border: '1px solid #d1d5db',
  fontSize: '12px', backgroundColor: 'white',
};

const QuantSignalsComponent: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { selectedPfId, canOrder } = useAuth();   // canOrder: 주문 권한 없으면 주문 생성 버튼 숨김
  const axiosInstance = getMainAxios();

  const [tab, setTab] = useState<'summary' | 'detail'>('summary');
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [summary, setSummary] = useState<QuantSummaryResponse | null>(null);
  const [signals, setSignals] = useState<QuantSignal[]>([]);
  const [filterSignal, setFilterSignal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const [summaryOrderLoading, setSummaryOrderLoading] = useState(false);
  const [previewOrders, setPreviewOrders] = useState<PreviewOrder[]>([]);
  const [previewApplied, setPreviewApplied] = useState(false);
  const [baseAmtInfo, setBaseAmtInfo] = useState<BaseAmtInfo | null>(null);

  const fetchDates = useCallback(async () => {
    if (!selectedPfId) return;
    try {
      const res = await axiosInstance.get('/api/v1/quant/dates', { params: { pfId: selectedPfId } });
      const d = res.data || [];
      setDates(d);
      if (d.length > 0 && !selectedDate) setSelectedDate(d[0]);
    } catch (err) {
      console.error('날짜 목록 조회 실패:', err);
    }
  }, [selectedPfId]);

  const fetchSummary = useCallback(async () => {
    if (!selectedPfId || !selectedDate) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/v1/quant/summary', {
        params: { pfId: selectedPfId, d: selectedDate },
      });
      setSummary(res.data);
    } catch (err) {
      console.error('요약 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPfId, selectedDate]);

  const fetchSignals = useCallback(async () => {
    if (!selectedPfId || !selectedDate) return;
    setLoading(true);
    try {
      const params: Record<string, any> = { pfId: selectedPfId, d0: selectedDate, d1: selectedDate };
      if (filterSignal !== null) params.signal = filterSignal;
      const res = await axiosInstance.get('/api/v1/quant/signals', { params });
      setSignals(res.data || []);
    } catch (err) {
      console.error('신호 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPfId, selectedDate, filterSignal]);

  useEffect(() => { fetchDates(); }, [fetchDates]);
  useEffect(() => {
    if (tab === 'summary') fetchSummary();
    else if (tab === 'detail') fetchSignals();
  }, [tab, selectedDate, fetchSummary, fetchSignals]);
  useEffect(() => { setPreviewOrders([]); setPreviewApplied(false); }, [selectedDate, selectedPfId]);

  const generatePreviewOrders = async () => {
    if (!selectedPfId || !selectedDate) return;
    if (selectedPfId === 0) { alert('주문은 개별 계좌를 선택해야 합니다.'); return; }
    setSummaryOrderLoading(true);
    setPreviewOrders([]);
    setPreviewApplied(false);
    try {
      const res = await axiosInstance.post('/api/v1/quant/generate-orders', null, {
        params: { pfId: selectedPfId, d: selectedDate, orderQuantity: 100, preview: true },
      });
      const orders: PreviewOrder[] = (res.data.orders || []).map((o: PreviewOrder) => ({ ...o, selected: true }));
      setPreviewOrders(orders);
      if (res.data.baseAmtInfo) setBaseAmtInfo(res.data.baseAmtInfo);
      if (orders.length === 0) alert('생성 가능한 주문이 없습니다.');
    } catch (err: any) {
      alert('예상주문 생성 실패: ' + (err.response?.data?.error || err.message));
    } finally {
      setSummaryOrderLoading(false);
    }
  };

  const applyPreviewOrders = async () => {
    if (!selectedPfId || !selectedDate) return;
    const selected = previewOrders.filter(o => o.selected);
    if (selected.length === 0) { alert('선택된 종목이 없습니다.'); return; }
    if (!confirm(`${selectedDate} 선택된 ${selected.length}건을 실제 주문으로 생성하시겠습니까? (기존 시뮬레이션 주문은 삭제 후 재생성됩니다)`)) return;
    setSummaryOrderLoading(true);
    try {
      const prodIds = selected.map(o => o.prodId).join(',');
      const res = await axiosInstance.post('/api/v1/quant/generate-orders', null, {
        params: { pfId: selectedPfId, d: selectedDate, orderQuantity: 100, prodIds },
      });
      const { orderCount, deleted, skipped } = res.data;
      setPreviewApplied(true);
      alert(`기존 ${deleted}건 삭제, ${orderCount}건 주문 생성${skipped > 0 ? `, ${skipped}건 매수불가 스킵` : ''}`);
    } catch (err: any) {
      alert('주문 적용 실패: ' + (err.response?.data?.error || err.message));
    } finally {
      setSummaryOrderLoading(false);
    }
  };

  const togglePreviewSelect = (prodId: string) => {
    setPreviewOrders(prev => prev.map(o => o.prodId === prodId ? { ...o, selected: !o.selected } : o));
  };
  const togglePreviewSelectAll = () => {
    const allSelected = previewOrders.every(o => o.selected);
    setPreviewOrders(prev => prev.map(o => ({ ...o, selected: !allSelected })));
  };

  const renderSummary = () => {
    if (!summary || summary.summary.length === 0) {
      return <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>해당 날짜에 데이터가 없습니다.</div>;
    }
    const strategies = summary.strategies;
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...thStyle, color: '#1e40af' }}>종목</th>
              <th style={{ ...thStyle, color: '#1e40af' }}>종가</th>
              {strategies.map(s => <th key={s} style={{ ...thStyle, color: '#1e40af' }}>{s}</th>)}
              <th style={{ ...thStyle, color: '#dc2626' }}>BUY</th>
              <th style={{ ...thStyle, color: '#2563eb' }}>SELL</th>
              <th style={{ ...thStyle, color: '#6b7280' }}>HOLD</th>
            </tr>
          </thead>
          <tbody>
            {summary.summary.map((row: QuantSummaryItem) => (
              <tr key={row.prodId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{row.prodNm || row.prodId}</div>
                  <div style={{ color: '#9ca3af', fontSize: '10px' }}>{row.prodId}</div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {row.lastClose.toLocaleString()}
                </td>
                {strategies.map(sName => {
                  const sig = row.signals.find(s => s.strategyName === sName);
                  if (!sig) return <td key={sName} style={{ ...tdStyle, textAlign: 'center' }}>-</td>;
                  return (
                    <td key={sName} style={{ ...tdStyle, textAlign: 'center', backgroundColor: signalBg(sig.signal) }}>
                      <div style={{ fontWeight: 700, color: signalColor(sig.signal), fontSize: '11px' }}>
                        {signalLabel(sig.signal)}
                      </div>
                      <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                        {(sig.confidence * 100).toFixed(0)}%
                      </div>
                    </td>
                  );
                })}
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{row.buyCount || '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#3b82f6' }}>{row.sellCount || '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center', color: '#6b7280' }}>{row.holdCount || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const previewColDefs: ColDef<PreviewOrder>[] = [
    {
      headerName: '', width: 40, pinned: 'left',
      headerComponent: () => {
        const allSelected = previewOrders.every(o => o.selected);
        return <input type="checkbox" checked={allSelected} onChange={togglePreviewSelectAll} />;
      },
      cellRenderer: (p: any) => (
        <input type="checkbox" checked={p.data.selected ?? false}
          onChange={() => togglePreviewSelect(p.data.prodId)} />
      ),
      sortable: false,
    },
    ...previewBaseColDefs,
  ];

  const detailColDefs: ColDef<QuantSignal>[] = [
    { headerName: '종목코드', field: 'prodId', width: 90 },
    { headerName: '종목명', field: 'prodNm', width: 100 },
    { headerName: '전략', field: 'strategyName', width: 130 },
    {
      headerName: '신호', field: 'signal', width: 80,
      cellRenderer: (p: any) => {
        const v = p.value;
        return `<span style="font-weight:700;color:${signalColor(v)}">${signalLabel(v)}</span>`;
      },
    },
    {
      headerName: '신뢰도', field: 'confidence', width: 80,
      valueFormatter: (p: any) => p.value != null ? `${(p.value * 100).toFixed(1)}%` : '',
    },
    {
      headerName: '종가', field: 'lastClose', width: 100, type: 'rightAligned',
      valueFormatter: (p: any) => p.value?.toLocaleString() ?? '',
    },
    { headerName: '사유', field: 'reason', flex: 1, minWidth: 200 },
    { headerName: '분석일', field: 'analysisDate', width: 100 },
    { headerName: '주문일', field: 'orderDate', width: 100 },
    { headerName: '생성시각', field: 'genT', width: 160 },
  ];

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {loading && <div style={{ marginBottom: '10px', fontSize: '11px', color: '#9ca3af' }}>로딩 중...</div>}

      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' }}>분석일:</label>
          <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={selectStyle}>
            {dates.map(d => <option key={d} value={d}>{d}</option>)}
            {dates.length === 0 && <option value="">데이터 없음</option>}
          </select>
          <span style={{ color: '#d1d5db' }}>|</span>

          <button onClick={() => setTab('summary')} style={tab === 'summary' ? tabActiveStyle : tabStyle}>요약</button>
          <button onClick={() => setTab('detail')} style={tab === 'detail' ? tabActiveStyle : tabStyle}>상세</button>

          {tab === 'detail' && (
            <>
              <span style={{ color: '#d1d5db' }}>|</span>
              <button onClick={() => setFilterSignal(null)}
                style={filterSignal === null ? filterActiveStyle : filterStyle}>전체</button>
              <button onClick={() => setFilterSignal(1)}
                style={filterSignal === 1 ? { ...filterActiveStyle, backgroundColor: '#ef4444' } : filterStyle}>BUY</button>
              <button onClick={() => setFilterSignal(2)}
                style={filterSignal === 2 ? { ...filterActiveStyle, backgroundColor: '#3b82f6' } : filterStyle}>SELL</button>
              <button onClick={() => setFilterSignal(0)}
                style={filterSignal === 0 ? { ...filterActiveStyle, backgroundColor: '#6b7280' } : filterStyle}>HOLD</button>
            </>
          )}
        </div>
      </div>

      {tab === 'summary' ? (
        <>
          <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden' }}>
            {renderSummary()}
            {summary && summary.summary.length > 0 && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                  {summary.summary.filter(r => (r.buyCount > r.sellCount && r.buyCount > r.holdCount) || (r.sellCount > r.buyCount && r.sellCount > r.holdCount)).length}개 종목 주문 가능
                </span>
                {canOrder && (
                <button onClick={generatePreviewOrders} disabled={summaryOrderLoading}
                  style={{
                    padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    backgroundColor: summaryOrderLoading ? '#9ca3af' : '#f59e0b', color: 'white',
                  }}>
                  {summaryOrderLoading ? '처리 중...' : '예상주문생성'}
                </button>
                )}
              </div>
            )}
          </div>

          {previewOrders.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ backgroundColor: '#fffbeb', border: '2px solid #f59e0b', borderBottom: 'none', borderRadius: '8px 8px 0 0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>
                    예상주문 미리보기 ({previewOrders.length}건, 선택 {previewOrders.filter(o => o.selected).length}건)
                  </span>
                  {baseAmtInfo && (
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                      계산기준({baseAmtInfo.bsDate}): D+2 {baseAmtInfo.n2TotAmt.toLocaleString()}원 → 매수가능 {baseAmtInfo.buyableAmt.toLocaleString()}원
                    </div>
                  )}
                </div>
                {previewApplied ? (
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#22c55e' }}>주문 적용 완료</span>
                ) : (
                  <button onClick={applyPreviewOrders}
                    disabled={summaryOrderLoading || previewOrders.filter(o => o.selected).length === 0}
                    style={{
                      padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      backgroundColor: (summaryOrderLoading || previewOrders.filter(o => o.selected).length === 0) ? '#9ca3af' : '#ef4444', color: 'white',
                    }}>
                    {summaryOrderLoading ? '처리 중...' : `주문적용 (${previewOrders.filter(o => o.selected).length}건)`}
                  </button>
                )}
              </div>
              <div className="ag-theme-quartz" style={{ height: Math.min(150 + previewOrders.length * 42, 400), width: '100%', border: '2px solid #f59e0b', borderTop: '1px solid #fde68a', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                <AgGridReact<PreviewOrder> {...copyMenu.gridProps}
                  rowData={previewOrders}
                  columnDefs={previewColDefs}
                  defaultColDef={{ resizable: true, sortable: true, filter: false }}
                  getRowId={p => p.data.prodId}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden' }}>
          <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
            <AgGridReact<QuantSignal> {...copyMenu.gridProps}
              rowData={signals}
              columnDefs={detailColDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              getRowId={p => String(p.data.id)}
              animateRows={true}
            />
          </div>
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default QuantSignalsComponent;
